import { type FC, useState, useMemo, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Swords, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui';
import { useAuth, useSiteLogo, useSiteSettings } from '@/hooks';
import { REGISTRATIONS_CLOSED, isClosedPath } from '@/lib/registrationsClosed';

/**
 * Main navigation component with responsive mobile menu
 * Shows different navigation items based on auth state
 * Dynamically includes active special event link if one exists
 */
export const Navigation: FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const [mobileLearnMoreOpen, setMobileLearnMoreOpen] = useState(false);
  const learnMoreRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut, needsPasswordReset } = useAuth();
  const { logoUrl } = useSiteLogo();
  const { settings } = useSiteSettings();

  // Redirect to reset password page if user needs to change their password
  useEffect(() => {
    if (!authLoading && user && needsPasswordReset && location.pathname !== '/reset-password') {
      navigate('/reset-password', { replace: true });
    }
  }, [authLoading, user, needsPasswordReset, location.pathname, navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (learnMoreRef.current && !learnMoreRef.current.contains(event.target as Node)) {
        setLearnMoreOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on route change
  useEffect(() => {
    setLearnMoreOpen(false);
    setMobileLearnMoreOpen(false);
  }, [location.pathname]);

  // Main nav items (not in Learn More)
  const navItems = useMemo(() => {
    const items: Array<{ path: string; label: string; external?: boolean }> = [
      { path: '/', label: 'HOME' },
      { path: '/event-registration', label: 'ENTRY' },
      { path: '/day-pass', label: 'DAY PASS' },
      { path: 'https://cactuscupstore.com', label: 'MERCH', external: true },
      { path: '/events', label: 'TOURNAMENTS' },
      { path: '/hotel', label: 'HOTELS' },
    ];

    // Filter out closed registration routes (ENTRY, DAY PASS) when registrations
    // are closed. Keyed on path, not label, so renaming a label in the future
    // cannot silently restore a live link.
    if (REGISTRATIONS_CLOSED) {
      return items.filter(item => !isClosedPath(item.path));
    }

    return items;
  }, []);

  // More sub-menu items
  const moreItems = useMemo(() => {
    const items: Array<{ path: string; label: string; external?: boolean }> = [];

    // Add About first (at top)
    items.push({
      path: '/about',
      label: 'ABOUT',
    });

    // Add Instructors
    items.push({
      path: '/instructors',
      label: 'INSTRUCTORS',
    });

    // Add Special Events (multiple events listing)
    items.push({
      path: '/special-events',
      label: 'SPECIAL EVENTS',
    });

    // Add Volunteer
    items.push({
      path: '/volunteer',
      label: 'VOLUNTEER',
    });

    // Add Sponsors & Vendors
    items.push({
      path: '/sponsors-vendors',
      label: 'SPONSORS & VENDORS',
    });

    // Add Order History (only for authenticated users)
    if (user) {
      items.push({
        path: '/orders',
        label: 'ORDER HISTORY',
      });
    }

    // Add Rules and Schedule (external link) - only if URL is configured
    if (settings.rulesUrl) {
      items.push({
        path: settings.rulesUrl,
        label: 'RULES & SCHEDULE',
        external: true,
      });
    }

    return items;
  }, [settings.rulesUrl, user]);

  const isMoreActive = moreItems.some(item => !item.external && location.pathname === item.path);

  const isActive = (path: string): boolean => location.pathname === path;

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      setMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  return (
    <nav className="bg-turquoise-700 shadow-lg border-b border-turquoise-600 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            {logoUrl ? (
              <>
                <img
                  src={logoUrl}
                  alt="Site logo"
                  className="h-10 sm:h-12 w-auto object-contain"
                />
                <div className="flex flex-col">
                  <span className="text-lg sm:text-2xl font-viking font-bold text-white leading-tight">Cactus Cup</span>
                  <span className="text-xs sm:text-sm text-white/90 leading-tight hidden xs:block">Mesa, AZ, July 17-19 2026</span>
                </div>
              </>
            ) : (
              <>
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-md">
                  <Swords className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg sm:text-2xl font-viking font-bold text-white leading-tight">Cactus Cup</span>
                  <span className="text-xs sm:text-sm text-white/90 leading-tight hidden xs:block">Mesa, AZ - July 17-19 2026</span>
                </div>
              </>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) =>
              item.external ? (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-viking text-sm font-medium transition-colors px-3 py-2 rounded-lg text-white hover:bg-turquoise-600"
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    font-viking text-sm font-medium transition-colors px-3 py-2 rounded-lg
                    ${
                      isActive(item.path)
                        ? 'bg-orange-500 text-white'
                        : 'text-white hover:bg-turquoise-600'
                    }
                  `}
                >
                  {item.label}
                </Link>
              )
            )}

            {/* More Dropdown */}
            <div className="relative" ref={learnMoreRef}>
              <button
                onClick={() => setLearnMoreOpen(!learnMoreOpen)}
                className={`
                  font-viking text-sm font-medium transition-colors px-3 py-2 rounded-lg flex items-center gap-1
                  ${
                    isMoreActive
                      ? 'bg-orange-500 text-white'
                      : 'text-white hover:bg-turquoise-600'
                  }
                `}
              >
                MORE
                {learnMoreOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>

              {learnMoreOpen && (
                <div className="absolute top-full left-0 mt-1 bg-turquoise-700 border border-turquoise-600 rounded-lg shadow-lg min-w-[180px] py-1 z-50">
                  {moreItems.map((item) =>
                    item.external ? (
                      <a
                        key={item.path}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block font-viking text-sm font-medium px-4 py-2 text-white hover:bg-turquoise-600 transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`
                          block font-viking text-sm font-medium px-4 py-2 transition-colors
                          ${
                            isActive(item.path)
                              ? 'bg-orange-500 text-white'
                              : 'text-white hover:bg-turquoise-600'
                          }
                        `}
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Auth buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button className="bg-orange-500 hover:bg-orange-600 text-white">Dashboard</Button>
                </Link>
                <Button onClick={handleSignOut} variant="secondary">
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button className="bg-gradient-to-br from-turquoise-600 to-turquoise-700 text-white border-2 border-orange-500 hover:from-turquoise-500 hover:to-turquoise-600">Sign In</Button>
                </Link>
                {!REGISTRATIONS_CLOSED && (
                  <Link to="/register">
                    <Button>Register</Button>
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-turquoise-600 transition-colors"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-turquoise-600">
            <div className="flex flex-col gap-1">
              {navItems.map((item) =>
                item.external ? (
                  <a
                    key={item.path}
                    href={item.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="font-viking text-sm font-medium px-3 py-2 rounded-lg transition-colors text-white hover:bg-turquoise-600"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`
                      font-viking text-sm font-medium px-3 py-2 rounded-lg transition-colors
                      ${
                        isActive(item.path)
                          ? 'bg-orange-500 text-white'
                          : 'text-white hover:bg-turquoise-600'
                      }
                    `}
                  >
                    {item.label}
                  </Link>
                )
              )}

              {/* Mobile More Collapsible */}
              <button
                onClick={() => setMobileLearnMoreOpen(!mobileLearnMoreOpen)}
                className={`
                  font-viking text-sm font-medium px-3 py-2 rounded-lg transition-colors flex items-center justify-between
                  ${
                    isMoreActive
                      ? 'bg-orange-500 text-white'
                      : 'text-white hover:bg-turquoise-600'
                  }
                `}
              >
                MORE
                {mobileLearnMoreOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>

              {mobileLearnMoreOpen && (
                <div className="ml-4 flex flex-col gap-1">
                  {moreItems.map((item) =>
                    item.external ? (
                      <a
                        key={item.path}
                        href={item.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="font-viking text-sm font-medium px-3 py-2 rounded-lg text-white hover:bg-turquoise-600 transition-colors"
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`
                          font-viking text-sm font-medium px-3 py-2 rounded-lg transition-colors
                          ${
                            isActive(item.path)
                              ? 'bg-orange-500 text-white'
                              : 'text-white hover:bg-turquoise-600'
                          }
                        `}
                      >
                        {item.label}
                      </Link>
                    )
                  )}
                </div>
              )}
              <div className="pt-4 border-t border-turquoise-600 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                        Dashboard
                      </Button>
                    </Link>
                    <Button onClick={handleSignOut} variant="secondary" className="w-full">
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-gradient-to-br from-turquoise-600 to-turquoise-700 text-white border-2 border-orange-500 hover:from-turquoise-500 hover:to-turquoise-600">
                        Sign In
                      </Button>
                    </Link>
                    {!REGISTRATIONS_CLOSED && (
                      <Link to="/event-registration" onClick={() => setMobileMenuOpen(false)}>
                        <Button className="w-full">Register</Button>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
